import { z } from 'zod'
import { ownerSchema } from '@/types/schema/api/owner-schema'
import { collaborativeItemTypeSchema } from '@/types/schema/collaborative-item'
import { genderSchema } from '@/types/schema/common/gender-schema'
import { willingnessToCollaborateSchema } from '@/types/schema/directory/kol-management'
import { followerLevelSchema } from '@/types/schema/follower-level'
import { performanceQuartileMetricsSchema } from '@/types/schema/performance-quartile-metrics-schema'
import { platformShortcodeSchema } from '@/types/schema/platform/platform-shortcode-schema'
import { PlatformShortcode } from '@/utils/convert-platform'

const predictiveReportPlatformShortcodeSchema = platformShortcodeSchema.or(
  z.literal('custom'),
)
type PredictiveReportPlatformShortcode = z.infer<
  typeof predictiveReportPlatformShortcodeSchema
>

const predictiveReportCustomDataSchema = z.object({
  like_count: z.number().nullish(),
  comment_count: z.number().nullish(),
  share_count: z.number().nullish(),
  view_count: z.number().nullish(),
  reach_count: z.number().nullish(),
  click_count: z.number().nullish(),
})

const predictiveReportCollaborativeItemMetaDataSchema = z.object({
  is_custom_platform: z.boolean(),
  is_custom_collaborative_item: z.boolean(),
  platform_name: platformShortcodeSchema.or(z.string()),
  collaborative_item: collaborativeItemTypeSchema.or(z.string()),
})
type PredictiveReportCollaborativeItemMetaData = z.infer<
  typeof predictiveReportCollaborativeItemMetaDataSchema
>

const predictiveReportCollaborativeItemSchema = z.object({
  kol_uuid: z.string().uuid(),
  name: z.string(),
  predictive_report_kol_id: z.number().nullish(),
  predictive_report_kol_collaborative_item_id: z.number().nullish(),
  metadata: predictiveReportCollaborativeItemMetaDataSchema,
  collaborative_items_count: z.number().min(1),
  cooperation_fee: z.number().min(0),
  ai_predicted_single_cooperation_fee: z.number().min(0).nullish().default(0),
  is_custom_data: z.boolean(),
  custom_data: predictiveReportCustomDataSchema,
})
type PredictiveReportCollaborativeItem = z.infer<
  typeof predictiveReportCollaborativeItemSchema
>

const predictiveReportSchema = z.object({
  id: z.number(),
  name: z.string(),
  kol_count: z.number(),
  update_at: z.string(),
  owner: ownerSchema,
})
type PredictiveReport = z.infer<typeof predictiveReportSchema>

const predictiveReportConfigSchema = z.object({
  predictive_report_id: predictiveReportSchema.shape.id,
  name: z.string(),
  owner: ownerSchema,
  customized_tags_names: z.string().array(),
  platforms: predictiveReportPlatformShortcodeSchema.array().max(4),
  ig: z.record(collaborativeItemTypeSchema, z.boolean()), // 建立報告時選擇的合作內容類型，會在首次加入人選時使用
  fb: z.record(collaborativeItemTypeSchema, z.boolean()),
  yt: z.record(collaborativeItemTypeSchema, z.boolean()),
  start_date: z.string().datetime(),
  end_date: z.string().datetime(),
  total_budget: z.number(),
  created_at: z.string().datetime(),
  updated_at: z.string().datetime(),
  used_collaborative_item_count: z.number(),
  kol_count: z.number(),
  is_custom_data_completed: z.boolean(),
  is_data_changed: z.boolean(),
  locked_candidate_list: z
    .object({ uuid: z.string().uuid(), name: z.string() })
    .array(),
})
type PredictiveReportConfig = z.infer<typeof predictiveReportConfigSchema>

const predictiveReportSubtotalSchema = z.object({
  estimate_costs: z.number().min(0),
  total_budget: z.number().min(0),
  kol_portfolios: z.record(followerLevelSchema, z.number()).nullish(),
  collaborative_items: z
    .object({
      metadata: predictiveReportCollaborativeItemMetaDataSchema,
      collaborative_items_count: z.number().min(0),
    })
    .array(),
  platform_estimate_reach: z
    .object({
      platform: predictiveReportPlatformShortcodeSchema,
      kpi: z.object({
        post: z.number().nullish(),
        video: z.number().nullish(),
        custom_value: z.number().nullish(),
      }),
    })
    .array(),
})
type PredictiveReportSubtotal = z.infer<typeof predictiveReportSubtotalSchema>

const predictiveReportCandidateSchema = z.object({
  predictive_report_kol_id: z.number(),
  name: z.string(),
  uuid: z.string().uuid(),
  platform: predictiveReportPlatformShortcodeSchema,
  is_selected: z.boolean(),
  collaborative_items: z
    .object({
      predictive_report_kol_collaborative_item_id: z.number(),
      metadata: predictiveReportCollaborativeItemMetaDataSchema,
      collaborative_items_count: z.number().min(0),
      cooperation_fee: z.number().min(0),
    })
    .array()
    .nonempty(), // 預期不可能是空的，因為空的會被後端過濾掉
  total_cooperation_fee: z.number().min(0),
  follower_level: followerLevelSchema.nullable(),
  kpi: z.object({
    follower_count: z.number().min(0).int().nullish(),
    engagement_rate: z.number().nullish(),
    view_rate: z.number().nullish(),
    follow_count_growth_rate: z.number().nullish(),
    engagement_rate_performance: performanceQuartileMetricsSchema.nullish(),
    view_rate_performance: performanceQuartileMetricsSchema.nullish(),
    follow_count_growth_rate_performance:
      performanceQuartileMetricsSchema.nullish(),
    image_estimated_reach_count: z.number().min(0).int().nullish(),
    video_estimated_reach_count: z.number().min(0).int().nullish(),
    main_audience: z.object({
      main_gender: z
        .object({
          code: genderSchema,
          weight: z.number().min(0),
        })
        .nullish(),
      main_age: z
        .object({
          code: z.string(), // proto: 0_12, 13_17, 18_24
          weight: z.number().min(0),
        })
        .nullish(),
    }),
    score: z.number().min(0).max(100).nullish(),
  }),
  posts: z
    .object({
      platform_type: z.nativeEnum(PlatformShortcode),
      crawler_id: z.string(),
      post_id: z.string(),
      ig_post_id: z.string(),
      link: z.string().url(),
      title: z.string(),
      content: z.string(),
      thumbnail_url: z.string().url(),
      like_count: z.number().nullish(),
      comment_count: z.number().nullish(),
      watch_count: z.number().nullish(),
      mentioned_uuids: z.string().uuid().array(),
    })
    .array(),
  recommend_reason: z.string().nullish(),
  willingness_to_collaborate: willingnessToCollaborateSchema,
  note: z.string().nullish(),
  customized_tags: z.string().array(),
})
type PredictiveReportCandidate = z.infer<typeof predictiveReportCandidateSchema>

const predictiveReportCandidatePlatformAnalysisSchema = z.object({
  predictive_report_kol_id: z.number(),
  uuid: z.string().uuid(),
  name: z.string(),
  collaborative_items: z
    .object({
      predictive_report_kol_collaborative_item_id: z.number(),
      metadata: predictiveReportCollaborativeItemMetaDataSchema,
      collaborative_items_count: z.number().min(0),
      cooperation_fee: z.number().min(0),
    })
    .array()
    .nonempty(), // 預期不可能是空的，因為空的會被後端過濾掉
  kpi: z.object({
    estimated_like_count: z.number().min(0).int().nullish(),
    estimated_comment_count: z.number().min(0).int().nullish(),
    estimated_share_count: z.number().min(0).int().nullish(),
    estimated_view_count: z.number().min(0).int().nullish(),
    estimated_avg_view_rate: z.number().min(0).nullish(),
    estimated_engagement_count: z.number().min(0).int().nullish(),
    estimated_engagement_rate: z.number().min(0).nullish(),
    estimated_reach_count: z.number().min(0).int().nullish(),
    estimated_reach_rate: z.number().min(0).nullish(),
    estimated_click_count: z.number().min(0).int().nullish(),
  }),
})
type PredictiveReportCandidatePlatformAnalysis = z.infer<
  typeof predictiveReportCandidatePlatformAnalysisSchema
>

export {
  predictiveReportCandidatePlatformAnalysisSchema,
  predictiveReportCandidateSchema,
  predictiveReportCollaborativeItemMetaDataSchema,
  predictiveReportCollaborativeItemSchema,
  predictiveReportConfigSchema,
  predictiveReportCustomDataSchema,
  predictiveReportPlatformShortcodeSchema,
  predictiveReportSchema,
  predictiveReportSubtotalSchema,
}
export type {
  PredictiveReport,
  PredictiveReportCandidate,
  PredictiveReportCandidatePlatformAnalysis,
  PredictiveReportCollaborativeItem,
  PredictiveReportCollaborativeItemMetaData,
  PredictiveReportConfig,
  PredictiveReportPlatformShortcode,
  PredictiveReportSubtotal,
}
